import { ClipboardList, SquareCheckBig } from "lucide-react";
import { useState } from "react";

import { useAcceptTerm } from "@/common/api";
import { Button, Checkbox, ClassicLink } from "@/common/components";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/common/components/ui/dialog";
import { useAuth, useToast } from "@/common/hooks";

interface UserAgreementDialogProps {
  isOpen: boolean;
  onAccept: () => void;
  onClose: () => void;
}

export function UserAgreementDialog({ isOpen, onAccept, onClose }: UserAgreementDialogProps) {
  const [isAgree1, setIsAgree1] = useState(false);
  const [isAgree2, setIsAgree2] = useState(false);
  const [isMailing, setIsMailing] = useState(false);

  const {
    auth: { id },
  } = useAuth();
  const { toast } = useToast();
  const { isPending, mutateAsync } = useAcceptTerm();

  const handleAccept = () => {
    mutateAsync({ data: { userId: String(id), isMailAgreement: isMailing } })
      .then(() => {
        toast({
          title: "Соглашение принято",
          description: "Вы успешно приняли соглашение",
        });

        onAccept();
      })
      .catch((error: unknown) => {
        toast({
          variant: "destructive",
          title: "Ошибка",
          description: error instanceof Error ? error.message : "Не удалось принять соглашение",
        });
      });
  };

  // overflow-scroll дает пустые скролл бары на windows поэтому добавляем -auto

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent
        className="md:max-w-[464px] max-w-[313px] min-h-[292px] overflow-scroll-auto"
        withoutCross
      >
        <DialogHeader className="text-left">
          <DialogTitle className="flex items-baseline gap-2 md:text-2xl text-xl font-semibold md:leading-7 leading-6">
            <ClipboardList className="flex-shrink-0  h-5 w-5" />
            Перед началом работы ознакомьтесь с документами
          </DialogTitle>
        </DialogHeader>
        {/* текущий отступ = header margin bottom 16-20px + div padding top 4-8px */}
        <div className="space-y-5 md:pt-2 pt-1">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="agreement1"
              checked={isAgree1}
              onCheckedChange={(checked) => setIsAgree1(!!checked)}
            />
            <label htmlFor="agreement1" className="text-xs font-semibold leading-4 cursor-pointer">
              Я прочитал(а) и принимаю условия{" "}
              <ClassicLink href="/user_agreement.pdf">пользовательского соглашения</ClassicLink>{" "}
              <span className="font-light">(обязательно)</span>
            </label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="agreement2"
              checked={isAgree2}
              onCheckedChange={(checked) => setIsAgree2(!!checked)}
            />
            <label htmlFor="agreement2" className="text-xs font-semibold leading-4 cursor-pointer">
              Я прочитал(а) и принимаю условия{" "}
              <ClassicLink href="/privacy_policy.pdf">
                политики обработки персональных данных
              </ClassicLink>{" "}
              <span className="font-light">(обязательно)</span>
            </label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="mailing"
              checked={isMailing}
              onCheckedChange={(checked) => setIsMailing(!!checked)}
            />
            <label htmlFor="mailing" className="text-xs font-semibold leading-4 cursor-pointer">
              Я прочитал(а) и принимаю условия рекламных рассылок
            </label>
          </div>
        </div>

        <div className="flex flex-col justify-end gap-3 md:mt-7 mt-5">
          <Button
            onClick={handleAccept}
            disabled={!isAgree1 || !isAgree2 || isPending}
            className="flex items-center gap-2 w-full"
          >
            <SquareCheckBig className="md:h-4 h-3 md:w-4 w-3" />
            {isPending ? "Обработка..." : "Принять и продолжить"}
          </Button>
          <Button type="button" variant="outline" onClick={onClose}>
            Отмена
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default UserAgreementDialog;
